import { ApiError, createAction } from 'redux-api-middleware';
import { State as StoreState } from '../types/State';
import { HTTP_GET } from './ApiAction';

export const LOAD_LOGIN_ALERTS = 'barmer-bonus-shared/login-alerts/LOAD' as const;
export const LOAD_LOGIN_ALERTS_SUCCESS = 'barmer-bonus-shared/login-alerts/SUCCESS' as const;
export const LOAD_LOGIN_ALERTS_FAILURE = 'barmer-bonus-shared/login-alerts/FAILURE' as const;
export const SET_LOGIN_ALERTS = 'barmer-bonus-shared/login-alerts/SET' as const;

type LoadLoginAlerts = { type: typeof LOAD_LOGIN_ALERTS; payload?: ApiError; error?: boolean };
type LoginAlertsUpdate = {
  type: typeof LOAD_LOGIN_ALERTS_SUCCESS;
  payload: LoginAlert[];
};
type LoginAlertsFailure = {
  type: typeof LOAD_LOGIN_ALERTS_FAILURE;
  payload?: ApiError;
  error?: boolean;
};
type SetLoginAlerts = { type: typeof SET_LOGIN_ALERTS; loginAlerts: LoginAlert[] };

export type Action = LoadLoginAlerts | LoginAlertsUpdate | LoginAlertsFailure | SetLoginAlerts;

export type LoginAlert = {
  id: string;
  message: string;
  from: string;
  until: string;
};

export const LOGIN_ALERTS_LOADING = 'loading' as const;
export const LOGIN_ALERTS_LOADED = 'loaded' as const;
export const LOGIN_ALERTS_ERROR = 'error' as const;
export const LOGIN_ALERTS_UNKNOWN = 'unknown' as const;

export type LoadState =
  | typeof LOGIN_ALERTS_LOADING
  | typeof LOGIN_ALERTS_LOADED
  | typeof LOGIN_ALERTS_ERROR
  | typeof LOGIN_ALERTS_UNKNOWN;

export type State = {
  loading: LoadState;
  loginAlerts: LoginAlert[];
  error?: ApiError;
};

const initialState: State = {
  loading: LOGIN_ALERTS_UNKNOWN,
  loginAlerts: [],
  error: undefined,
};

export default function reducer(state: State = initialState, action: Action): State {
  switch (action.type) {
    case LOAD_LOGIN_ALERTS:
      return {
        ...state,
        loading: action.error === undefined ? LOGIN_ALERTS_LOADING : LOGIN_ALERTS_ERROR,
        error: action.error ? action.payload : undefined,
      };
    case LOAD_LOGIN_ALERTS_SUCCESS:
      return {
        ...state,
        loading: LOGIN_ALERTS_LOADED,
        loginAlerts: action.payload,
        error: undefined,
      };
    case LOAD_LOGIN_ALERTS_FAILURE:
      return {
        ...state,
        loading: LOGIN_ALERTS_ERROR,
        error: action.payload,
      };
    case SET_LOGIN_ALERTS:
      // login alerts can only be set when we did not get a response from the server
      if (state.loading !== LOGIN_ALERTS_LOADED) {
        return { ...state, loginAlerts: action.loginAlerts };
      }
      return state;
    default:
      return state;
  }
}

export function isLoaded(globalState: StoreState): boolean {
  return (
    globalState.loginAlerts &&
    (LOGIN_ALERTS_LOADED === globalState.loginAlerts.loading ||
      LOGIN_ALERTS_ERROR === globalState.loginAlerts.loading)
  );
}

export function isLoading(globalState: StoreState): boolean {
  return LOGIN_ALERTS_LOADING === globalState.loginAlerts?.loading;
}

export function hasLoginAlerts(globalState: StoreState): boolean {
  return globalState.loginAlerts?.loginAlerts.length > 0;
}

export function getCurrentLoginAlert(
  alerts: LoginAlert[],
  now: Date = new Date(),
): LoginAlert | undefined {
  return alerts.find((alert) => new Date(alert.from) <= now && new Date(alert.until) > now);
}

/**
 * Used to set login alerts manually from local cache
 */
export const setLoginAlerts = (loginAlerts: LoginAlert[]) => {
  return {
    type: SET_LOGIN_ALERTS,
    loginAlerts,
  };
};

/**
 * Load Login Alerts
 */
export const loadLoginAlerts = () =>
  createAction({
    endpoint: '/login-alerts',
    types: [LOAD_LOGIN_ALERTS, LOAD_LOGIN_ALERTS_SUCCESS, LOAD_LOGIN_ALERTS_FAILURE],
    method: HTTP_GET,
    bailout: (state: StoreState) => {
      return isLoaded(state) || isLoading(state);
    },
  });
